import { Navigate, Outlet } from "react-router-dom";
import {
  selectCurrentAccessToken,
  selectCurrentCode,
  selectCurrentRefreshToken,
  selectCurrentUser,
  setUserProfileDispatch,
  setCredentials,
} from "./authSlice";
import { UserGroup } from "../../common/User";
import { useGetAuthInfoMutation } from "../api/auth";
import {
  AuthPostBody,
  AuthRefreshPostBody,
  AuthResponseBody,
} from "../../common/Auth";
import { mapKeys, camelCase, snakeCase } from "lodash";
import { setCode } from "./authSlice";
import { useDispatch, useSelector } from "react-redux";
import jwt_decode from "jwt-decode";
import { AnyAction, Dispatch } from "redux";
import { useGetUserProfileDispatchQuery } from "../api/user";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useEffect } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useGetVesselEmailsQuery } from "../api/vessel";

const RequireAuth = ({
  allowedUserGroups,
}: {
  allowedUserGroups: UserGroup[];
}) => {
  const [authInfo] = useGetAuthInfoMutation();
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);

  const searchParams = new URL(window.location.href).searchParams;
  const currentCode = useSelector(selectCurrentCode);
  const currentAccessToken = useSelector(selectCurrentAccessToken);
  const currentRefreshToken = useSelector(selectCurrentRefreshToken);

  const { data: userProfileDispatch, isLoading } =
    useGetUserProfileDispatchQuery(!!currentAccessToken ? {} : skipToken, {
      refetchOnMountOrArgChange: true,
    });

  const shouldFetchVesselEmails = [
    UserGroup.MARINERS,
    UserGroup.HUMAN_RESOURCES,
    UserGroup.HUMAN_RESOURCES_ADMINS,
  ].includes(
    user?.userGroup as UserGroup
  );

  const { data: vesselEmails } = useGetVesselEmailsQuery(
    shouldFetchVesselEmails ? {} : skipToken
  );

  useEffect(() => {
    if (vesselEmails) {
      dispatch({
        type: "vesselEmail/setVesselEmails",
        payload: vesselEmails,
      });
    }
    vesselEmails?.map(i => {
      return true;
    })
  }, [vesselEmails, dispatch]);

  useEffect(() => {
    if (!currentAccessToken && currentCode === "refreshed") {
      dispatch(setCode("ok"));
      const payload = _formatAuthRefreshBody(currentRefreshToken);
      _login(dispatch, authInfo, payload, currentRefreshToken);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAccessToken, currentCode]);

  useEffect(() => {
    if (!isLoading && !!currentAccessToken) {
      dispatch(setUserProfileDispatch(userProfileDispatch));
    }
  }, [currentAccessToken, dispatch, isLoading, userProfileDispatch]);

  if (!currentCode && searchParams.has("code")) {
    dispatch(setCode(searchParams.get("code")));
    const payload = _formatAuthBody(searchParams.get("code") as string);
    _login(dispatch, authInfo, payload, currentRefreshToken);
  } else if (
    !currentAccessToken &&
    (!currentCode || currentCode === "invalidated")
  ) {
    return <Navigate to="/landing" />;
  }

  if (
    !isLoading &&
    (user?.userGroup as UserGroup) === UserGroup.MARINERS &&
    !userProfileDispatch?.profile &&
    window.location.pathname !== "/user"
  ) {
    return <Navigate to="/user" />;
  }

  return allowedUserGroups.includes(user?.userGroup as UserGroup) &&
    userProfileDispatch?.captain !== undefined ? (
    <Outlet />
  ) : (
    // TODO: flesh out what this should look like
    <Box sx={{ display: "flex" }}>
      <CircularProgress />
    </Box>
  );
};

function _formatAuthBody(code: string): string {
  const authPostBody: AuthPostBody = {
    grantType: "authorization_code",
    clientId: process.env.REACT_APP_COGNITO_CLIENT_ID as string,
    redirectURI: process.env.REACT_APP_COGNITO_REDIRECT_URI as string,
    code: code,
  };
  const formattedBody = mapKeys(authPostBody, (value, key) => snakeCase(key));
  return Object.entries(formattedBody)
    .map(
      ([key, value]) =>
        encodeURIComponent(key) + "=" + encodeURIComponent(value)
    )
    .join("&");
}

function _formatAuthRefreshBody(refreshToken: string): string {
  const authPostBody: AuthRefreshPostBody = {
    grantType: "refresh_token",
    clientId: process.env.REACT_APP_COGNITO_CLIENT_ID as string,
    redirectURI: process.env.REACT_APP_COGNITO_REDIRECT_URI as string,
    refreshToken: refreshToken,
  };
  const formattedBody = mapKeys(authPostBody, (value, key) => snakeCase(key));
  return Object.entries(formattedBody)
    .map(
      ([key, value]) =>
        encodeURIComponent(key) + "=" + encodeURIComponent(value)
    )
    .join("&");
}

async function _login(
  dispatch: Dispatch<AnyAction>,
  authInfo: any,
  payload: string,
  refreshToken: string
) {
  let response: any = await authInfo(payload);
  const authResponse = mapKeys(response.data, (value, key) =>
    camelCase(key)
  ) as AuthResponseBody;
  const userInfo: any = jwt_decode(authResponse.idToken);
  const sessionInfo = {
    accessToken: authResponse.accessToken,
    idToken: authResponse.idToken,
    refreshToken: !!authResponse.refreshToken
      ? authResponse.refreshToken
      : refreshToken,
    user: {
      username: userInfo["cognito:username"],
      userGroup: userInfo["cognito:groups"][0].slice(0, -1) as UserGroup,
      name: userInfo.name,
      email: userInfo.email,
      birthdate: userInfo.birthdate,
    },
  };
  dispatch(setCredentials(sessionInfo));
}

export default RequireAuth;
